import { useLazyQuery } from "@apollo/client";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { routers } from "config/routes";
import useStore from "config/store";
import { GET_COMPANY_WITH_ACCOUNTING_FIRM } from "queries/centralapi/companies";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  CompanyWithAccountingFirmInput,
  CompanyWithAccountingFirmOutput,
} from "types/queries/centralapi/accountingFirm";
import { getLastCompany } from "utils/lastCompany";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { validateId } from "utils/validateId";

export function useQueryCompanyAccountingFirm() {
  const location = useLocation();

  const setCompanySelected = useStore(state => state.setCompanySelected);
  const setAccountingFirmSelected = useStore(state => state.setAccountingFirmSelected);
  const setIsCompanySelectOpen = useStore(state => state.setIsCompanySelectOpen);
  const { companyId } = useParams();
  const companySelected = useStore(state => state.companySelected);
  const userData = useStore(state => state.userData);
  const userIsClient = ["client", "client_without_accounting"].includes(
    userData?.userCategory.slug ?? "",
  );

  const [companyWithAccountingFirm, { data, error }] = useLazyQuery<
    CompanyWithAccountingFirmOutput,
    CompanyWithAccountingFirmInput
  >(GET_COMPANY_WITH_ACCOUNTING_FIRM, {
    client: clientCentralApiStrawberry,
  });

  useEffect(() => {
    if (data?.getCompany) {
      const { accountingFirm, ...company } = data.getCompany;
      setCompanySelected({
        id: company.id,
        name: company.name,
        internalReference: company.internalReference,
      });
      if (accountingFirm) {
        setAccountingFirmSelected({ id: accountingFirm.id, name: accountingFirm.name });
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) onErrorGraphql(error);
  }, [error]);

  useEffect(() => {
    if (userIsClient) {
      const companies = userData.companies ?? [];
      const hasMultipleCompanies = companies.length > 1;

      let companyToFetch: string | undefined = companies[0];

      // Clients with access to more than one dossier default to the last
      // accessed company saved in local storage, if still accessible.
      if (hasMultipleCompanies) {
        const lastCompany = getLastCompany(userData?.id);
        if (lastCompany && companies.includes(lastCompany)) {
          companyToFetch = lastCompany;
        } else {
          companyToFetch = undefined;
        }
      }

      if (companyId && !companySelected) {
        companyToFetch = companyId;
      }

      if (validateId(companyToFetch)) {
        companyWithAccountingFirm({
          variables: {
            id: companyToFetch,
          },
        });

        if (location.pathname === `/` || companyId !== companyToFetch) {
          routers.navigate(`/company/${companyToFetch}`, { replace: true });
        }

        setIsCompanySelectOpen(false);
      } else if (hasMultipleCompanies && !companySelected) {
        setIsCompanySelectOpen(true);
      }
    }
  }, []);

  return {
    companyWithAccountingFirm,
    data,
  };
}
